import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import {
  Alert,
  CircularProgress,
  AppBar,
  Box,
  Button,
  Collapse,
  Dialog,
  IconButton,
  Link,
  Paper,
  Toolbar,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import RequestPageOutlinedIcon from "@mui/icons-material/RequestPageOutlined";
import { PagePaper } from "@/components/Paper";
import { HEADER_HEIGHT } from "@/features/header";
import { PageTitle } from "@/components/Title";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { CORPORATE_MASTER_LIST_LIMIT } from "../constants";
import {
  buildCorporateListQuery,
  downloadCorporateMasterExcel,
  getCorporateMasterList,
} from "../api";
import { useCorporateMasterList } from "../hooks/useCorporateMasterList";
import { useCorporateSelection } from "../hooks/useCorporateSelection";
import { CorporateMasterResults } from "../components/CorporateMasterResults";
import { CorporateSelectionBar } from "../components/CorporateSelectionBar";
import { CorporateMasterSearchCondition } from "../components/CorporateMasterSearchCondition";
import {
  corporateMasterSearchSchema,
  type CorporateMasterSearchConditions,
} from "../components/corporateMasterSearchSchema";
import {
  conditionsToParams,
  hasCorporateSearchCondition,
  defaultSearchConditions,
  paramsToConditions,
  toApiParams,
} from "../utils/searchParams";
import { listCompactSx } from "../utils/styles";

export const CorporateMasterList: React.FC = () => {
  // 検索条件の正本はURL。履歴移動時もフォームを同期する。
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useForm<CorporateMasterSearchConditions>({
    defaultValues: paramsToConditions(searchParams),
    resolver: zodResolver(corporateMasterSearchSchema),
  });
  const { reset } = form;
  // 候補選択で自動入力した法人番号。SPのモーダルを閉じても消えないよう、フォームと同じ親で持つ。
  const suggestedNumberRef = useRef<string | null>(null);
  useEffect(() => {
    reset(paramsToConditions(searchParams));
  }, [searchParams, reset]);
  const conditions = paramsToConditions(searchParams);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // 法人名から商業登記を取得する画面は、このフラグが有効なときだけルートがある。
  const { commercialBookList } = useFeatureFlags();
  // SPは検索項目が多く縦に長くなるため、既定で検索パネルを折りたたむ。
  const [isSearchExpanded, setIsSearchExpanded] = useState(!isMobile);
  // SPは検索フォームをフルスクリーンモーダルで開き、検索実行で閉じて結果を見せる。
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const apiParams = toApiParams(conditions);
  const hasConditions = hasCorporateSearchCondition(apiParams);
  const [recentPage, setRecentPage] = useState(0);
  const [recentPageSize, setRecentPageSize] = useState(20);
  useEffect(() => {
    setRecentPage(0);
  }, [searchParams]);
  const { apiClient } = useApiClient();
  // 選択はページの移動や表示形式の切替では残し、検索条件が変わったら外す。
  const selection = useCorporateSelection(searchParams.toString());
  const { data, error, isLoading, mutate } = useCorporateMasterList(
    hasConditions
      ? apiParams
      : {
          ...apiParams,
          limit: recentPageSize,
          offset: recentPage * recentPageSize,
        }
  );

  // 条件ありの一覧は検索結果（最大1000件）を一度に読み込む。条件なしはページだけのため、
  // 最新1000件の法人番号を読み直してから選ぶ。
  // 読み込み中（selection.isReplacing）・出力中は、ページを移って選択の行が作り直されても残るよう、画面の側で持つ。
  const [isExporting, setIsExporting] = useState(false);
  const selectAll = async (): Promise<void> => {
    const replace = selection.beginReplace();
    try {
      const results = hasConditions
        ? data?.results ?? []
        : (
            await getCorporateMasterList(
              apiClient,
              buildCorporateListQuery({
                ...apiParams,
                limit: CORPORATE_MASTER_LIST_LIMIT,
                offset: 0,
              })
            )
          ).results;
      replace.apply(
        results.map((item, position) => ({
          corporateNumber: item.corporateNumber,
          position,
        }))
      );
    } catch {
      if (replace.isCurrent()) {
        toast.error("検索結果の選択に失敗しました。再試行してください。");
      }
    } finally {
      replace.finish();
    }
  };
  const exportExcel = async (): Promise<void> => {
    setIsExporting(true);
    try {
      await downloadCorporateMasterExcel(
        apiClient,
        selection.orderedCorporateNumbers
      );
    } catch {
      toast.error("Excelの出力に失敗しました。再試行してください。");
    } finally {
      setIsExporting(false);
    }
  };

  const search = (values: CorporateMasterSearchConditions): void => {
    // 検索条件をURLへ反映（trim/空値除去はconditionsToParams側で実施）。
    setRecentPage(0);
    setSearchParams(conditionsToParams(values));
    // SPのモーダル検索は実行後に閉じて結果（カード）を見せる。
    setIsSearchModalOpen(false);
    // 下までスクロールした状態で検索しても、結果の先頭から見せる。
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const clear = (): void => {
    setRecentPage(0);
    form.reset(defaultSearchConditions);
    suggestedNumberRef.current = null;
    setSearchParams(new URLSearchParams());
  };

  // 検索フォーム本体。PCはサイドパネル、SPはモーダル内に同じものを表示する。
  const searchForm = (
    <CorporateMasterSearchCondition
      form={form}
      suggestedNumberRef={suggestedNumberRef}
      onSearch={search}
      onClear={clear}
    />
  );

  const compactSx = isMobile ? listCompactSx : undefined;

  return (
    <PagePaper>
      <Stack spacing={2} sx={compactSx}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <DomainOutlinedIcon color="primary" />
          <PageTitle>法人情報を検索する</PageTitle>
          {commercialBookList ? (
            <Link
              component={RouterLink}
              to="/commercialbook"
              variant="body2"
              color="text.secondary"
              underline="hover"
              sx={{
                ml: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <RequestPageOutlinedIcon fontSize="inherit" aria-hidden />
              商業登記簿を取得する
            </Link>
          ) : null}
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {!isMobile && (
            <Box
              sx={{
                width: 320,
                flexShrink: 0,
                position: "sticky",
                // 共通ヘッダー（sticky）の下に留める。
                top: HEADER_HEIGHT + 16,
                // 検索項目が画面より長いと sticky が効かず結果と一緒に流れるため、
                // ヘッダーを除いた画面の高さに収めてパネル内でスクロールさせる。
                maxHeight: `calc(100vh - ${HEADER_HEIGHT + 32}px)`,
                overflowY: "auto",
              }}
            >
              <Paper variant="outlined" sx={{ boxShadow: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    条件を指定
                  </Typography>
                  <IconButton
                    type="button"
                    aria-label="検索条件の表示切替"
                    aria-expanded={isSearchExpanded}
                    aria-controls="corporate-master-search-content"
                    onClick={() => {
                      setIsSearchExpanded((expanded) => !expanded);
                    }}
                  >
                    {isSearchExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Stack>
                <Collapse
                  in={isSearchExpanded}
                  id="corporate-master-search-content"
                >
                  {searchForm}
                </Collapse>
              </Paper>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <Stack spacing={2}>
              {isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => {
                    setIsSearchModalOpen(true);
                  }}
                  sx={{ alignSelf: "flex-start" }}
                >
                  検索条件を指定
                </Button>
              )}
              {!hasConditions && (
                <Typography variant="body2" color="text.secondary">
                  更新日の新しい1000件を表示中
                </Typography>
              )}
              {isLoading ? (
                <CircularProgress aria-label="検索中" />
              ) : error ? (
                <Alert
                  severity="error"
                  action={
                    <Button
                      onClick={() => {
                        mutate().catch(() => undefined);
                      }}
                    >
                      再試行
                    </Button>
                  }
                >
                  法人情報の取得に失敗しました。
                </Alert>
              ) : (
                <>
                  {(data?.results.length ?? 0) > 0 && (
                    <CorporateSelectionBar
                      selectedCount={selection.selected.size}
                      selectableCount={Math.min(
                        data?.count ?? 0,
                        CORPORATE_MASTER_LIST_LIMIT
                      )}
                      isSelectingAll={selection.isReplacing}
                      isExporting={isExporting}
                      onSelectAll={() => {
                        void selectAll();
                      }}
                      onClear={selection.clear}
                      onExport={() => {
                        void exportExcel();
                      }}
                    />
                  )}
                  <CorporateMasterResults
                    key={searchParams.toString()}
                    items={data?.results ?? []}
                    totalCount={data?.count ?? 0}
                    limit={CORPORATE_MASTER_LIST_LIMIT}
                    selectedRegistryTagCodes={conditions.registryTag}
                    selection={selection}
                    serverPagination={
                      hasConditions
                        ? undefined
                        : {
                            page: recentPage,
                            pageSize: recentPageSize,
                            onPageChange: setRecentPage,
                            onPageSizeChange: (size) => {
                              setRecentPageSize(size);
                              setRecentPage(0);
                            },
                          }
                    }
                  />
                </>
              )}
            </Stack>
          </Box>
        </Box>
        {isMobile && (
          <Dialog
            fullScreen
            open={isSearchModalOpen}
            onClose={() => {
              setIsSearchModalOpen(false);
            }}
            aria-labelledby="corporate-master-search-dialog-title"
          >
            <AppBar
              position="sticky"
              color="default"
              elevation={1}
              sx={{ bgcolor: "background.paper" }}
            >
              <Toolbar>
                <Typography
                  id="corporate-master-search-dialog-title"
                  variant="h6"
                  color="primary"
                  sx={{ flex: 1, fontSize: "1.1rem" }}
                >
                  法人情報を検索
                </Typography>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="検索を閉じる"
                  onClick={() => {
                    setIsSearchModalOpen(false);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Box sx={compactSx}>{searchForm}</Box>
          </Dialog>
        )}
      </Stack>
    </PagePaper>
  );
};
